import {
  Alert,
  AlertTitle,
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@mui/material";
import FormDateField from "components/form/FormDateField";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import FormCheckboxField from "../../../components/form/FormCheckboxField";
import FormSelectField from "../../../components/form/FormSelectField";
import FormTextField from "../../../components/form/FormTextField";
import { AgeRestriction, ofEvent } from "../../../types/event";
import { PublishAtFields } from "./PublishAtFields";
import { MembershipTypeSelect } from "../../../components/form/MembershipTypeSelect";

export function SettingsAndVisibilitySection() {
  const { control, watch, getValues, setValue } = useFormContext();
  const draft = watch("draft");
  const publishDate: Date = new Date(getValues("publishAt"));
  const [editScheduleDate, setEditScheduleDate] = useState(false);
  const [initialScheduleDate, setInitialScheduleDate] = useState(null);
  const publishAt = watch("publishAt");
  const options: Intl.DateTimeFormatOptions = {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  };

  useEffect(() => {
    if (!editScheduleDate) setInitialScheduleDate(publishAt);
  }, [publishAt, editScheduleDate]);

  return (
    <div>
      <Typography variant="h6">Settings</Typography>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2 }}>
        Visibility and access
      </Typography>
      <FormCheckboxField
        name={ofEvent("private")}
        control={control}
        label={"Private"}
        helperText="Hidden from public event lists. With allowed membership types, it appears in matching members' event lists. The direct link remains accessible."
      />
      <Box display="flex" flexDirection="row" gap={4}>
        <MembershipTypeSelect />
      </Box>
      <Alert severity="info" sx={{ mt: 1, mb: 3 }}>
        <AlertTitle>How visibility and attendance settings work</AlertTitle>
        <Box component="ul" sx={{ my: 0, pl: 2.5 }}>
          <li>
            <strong>Public event:</strong> Listed for everyone. Allowed
            membership types only restrict attendance.
          </li>
          <li>
            <strong>Private with allowed membership types:</strong> Listed only
            for matching members.
          </li>
          <li>
            <strong>Private without allowed membership types:</strong> Hidden
            from all event lists.
          </li>
        </Box>
        The direct event link remains accessible in all cases.
      </Alert>
      <Typography variant="subtitle1" fontWeight={600}>
        Event restrictions
      </Typography>
      <Box display="flex" flexDirection="row" gap={4}>
        <FormSelectField
          name={ofEvent("ageRestriction")}
          control={control}
          allowEmpty={true}
          label={"Age restriction"}
          list={Object.values(AgeRestriction)}
        />
        <FormTextField
          name={ofEvent("maxGuestCount")}
          label="Maximum number of guests"
          control={control}
          type="number"
        />
      </Box>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2 }}>
        Publishing
      </Typography>

      {!draft && initialScheduleDate && publishDate > new Date() ? (
        <>
          <Typography>
            {"This event is scheduled to be published at: " +
              new Date(initialScheduleDate).toLocaleDateString(
                "en-US",
                options
              ) +
              " " +
              new Date(initialScheduleDate).toLocaleTimeString("en-US")}
          </Typography>
          <Box display="flex" flexDirection="row" gap={4} alignItems={"end"}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={editScheduleDate}
                  onChange={(_, checked) => {
                    setEditScheduleDate(checked);
                    !checked && setValue("publishAt", initialScheduleDate);
                  }}
                />
              }
              label={"Edit Schedule Date"}
            />
            <FormDateField
              name="publishAt"
              control={control}
              disabled={!editScheduleDate}
              label={"Change publish date: "}
              dateProps={{
                disablePast: true,
              }}
              additionalRules={{
                validate: (date, _) =>
                  (date ? new Date(date) >= new Date() : true) ||
                  "Publish date must be in the future",
              }}
            />
          </Box>
        </>
      ) : (
        !draft &&
        initialScheduleDate && (
          <Typography>
            {"This event was published at: " +
              new Date(initialScheduleDate).toLocaleDateString(
                "en-US",
                options
              ) +
              " " +
              new Date(initialScheduleDate).toLocaleTimeString("en-US")}
          </Typography>
        )
      )}
      {draft && <PublishAtFields />}
    </div>
  );
}
